// 施工箇所(ConstructionPlanArea)詳細 — 閲覧。基本情報 + 工程一覧 + 関連施工(アコーディオン)。
import { useMemo, useState } from "react";
import {
  Box, Stack, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  Button, Chip, LinearProgress, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useParams } from "react-router-dom";
import { PageContext } from "../PageContext";
import { useFullDb } from "../../../api/dbSlice";
import { InfoList, InfoListColDef } from "../../../components/InfoList";
import { deriveAreaProcessCompletions } from "../../../types/derivedSelectors";
import { useMountSync } from "../../../util/useMountSync";
import { ContentLoading } from "../../../components/Loading";
import {
  getAreaProcessViews, getAreaStatus, AREA_STATUS_COLOR,
} from "../../../util/areaStatus";
import { deriveMixRatioLabel } from "../../../util/mixRatioLabel";
import { useCreatorName } from "../../../util/useCreatorName";
import { dataEntityText, MasterLink, DataLink } from "../../../components/links/EntityLinks";
import { formatDate as fmtDate, formatDateTime as fmtDateTime } from "../../../util/dateFormat";
import { constructionCompletionState, constructionStatusLabel, deriveMixedAt } from "../../../util/constructionStatus";
import type { ConstructionBase } from "../../../schema/generated/Construction";
import { StatusAccordion } from "../../../components/StatusAccordion";
import { errorMessageOf } from "../../../util/typed";

// 施工箇所詳細 — 削除確認ダイアログ
function DeleteDialog({
  open,
  label,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  label: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>削除の確認</DialogTitle>
      <DialogContent>
        <DialogContentText>{label} を削除します。よろしいですか？</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>キャンセル</Button>
        <Button color="error" onClick={onConfirm}>削除</Button>
      </DialogActions>
    </Dialog>
  );
}

// 関連施工実施を1行アコーディオンで表示するサブコンポーネント
function ConstructionAccordionRow({ con, db }: { con: ConstructionBase; db: ReturnType<typeof useFullDb>["db"] }) {
  const peRows = useMemo(() => {
    return db.processExecutions
      .filter((p) => p.deleted_at == null && p.construction_id === con.id)
      .map((p) => {
        const area = db.constructionPlanAreas.find((a) => a.id === p.construction_plan_area_id);
        const wd = db.workDetails.find((w) => w.id === p.work_detail_id);
        const areaLoc = area ? (() => {
          const slots = db.constructionPlanAreaLocations
            .filter((l) => l.deleted_at == null && l.construction_plan_area_id === area.id)
            .sort((a, b) => a.slot - b.slot);
          const names: string[] = [];
          for (const s of slots) {
            const div = db.locationDivisions.find((d) => d.id === s.location_division_id);
            if (div !== undefined) {
              names.push(div.division_name);
            }
          }
          return names.join(" / ");
        })() : "";
        return {
          id: p.id,
          areaId: p.construction_plan_area_id,
          areaLoc,
          wdName: wd?.name ?? "工程?",
          completed: p.completed_at != null,
        };
      });
  }, [db, con.id]);

  const peForStatus = db.processExecutions.filter((p) => p.deleted_at == null && p.construction_id === con.id);
  const mixedAt = deriveMixedAt(con.id, db.stirBatches);
  const compState = constructionCompletionState({ finished_at: con.finished_at, processExecutions: peForStatus });
  const statusLabel = constructionStatusLabel(compState);
  const statusColor = compState === "completed" ? "success" as const : compState === "draft" ? "default" as const : "warning" as const;

  return (
    <StatusAccordion header={
      <>
          <DataLink type="construction" id={con.id} onClick={(e) => e.stopPropagation()} />
          {con.executed_at != null && (
            <Chip size="small" variant="outlined" label={`施工日 ${fmtDate(con.executed_at)}`} />
          )}
          <Chip size="small" variant="outlined" color={statusColor}
            icon={compState === "completed" ? <CheckCircleIcon /> : undefined}
            label={statusLabel} />
          <Chip size="small" variant="outlined" label={`工程実施 ${peRows.length} 件`} />
          {mixedAt != null && (
            <Chip size="small" variant="outlined" label={`攪拌 ${fmtDateTime(mixedAt)}`} />
          )}
          {con.finished_at != null && (
            <Chip size="small" variant="outlined" label={`施工終了 ${fmtDateTime(con.finished_at)}`} />
          )}
      </>
    }>
        {peRows.length === 0 ? (
          <Typography variant="caption" color="text.secondary">工程実施はありません</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>施工箇所</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>工程</TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="right">完了状態</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {peRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                      <DataLink type="plan-area" id={row.areaId} />
                      {row.areaLoc && <Typography variant="body2" color="text.secondary">{row.areaLoc}</Typography>}
                    </Stack>
                  </TableCell>
                  <TableCell>{row.wdName}</TableCell>
                  <TableCell align="right">
                    <Chip size="small" color={row.completed ? "success" : "default"}
                      variant={row.completed ? "filled" : "outlined"}
                      icon={row.completed ? <CheckCircleIcon /> : undefined}
                      label={row.completed ? "完了" : "未完了"} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
    </StatusAccordion>
  );
}

// 施工実施一覧 — この施工箇所に工程実施が1件以上ある施工実施を列挙
function RelatedConstructionsTable({ db, areaId }: { db: ReturnType<typeof useFullDb>["db"]; areaId: string }) {
  const conRows = useMemo(() => {
    const conIds = new Set(
      db.processExecutions
        .filter((p) => p.deleted_at == null && p.construction_plan_area_id === areaId)
        .map((p) => p.construction_id),
    );
    return Array.from(conIds)
      .map((cid) => db.constructions.find((c) => c.id === cid))
      .filter((c): c is NonNullable<typeof c> => c != null && c.deleted_at == null)
      .sort((a, b) => (b.executed_at ?? 0) - (a.executed_at ?? 0));
  }, [db, areaId]);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="baseline" gap={1} mb={1}>
        <Typography variant="h6" fontWeight={700}>施工実施一覧</Typography>
        <Typography variant="caption" color="text.secondary">この施工箇所を含む施工実施</Typography>
        <Box flex={1} />
        <Chip size="small" label={`${conRows.length} 件`} color={conRows.length > 0 ? "primary" : "default"} />
      </Stack>
      {conRows.length === 0 ? (
        <Box sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
          <Typography variant="body2">この施工箇所を対象とする施工実施はまだありません</Typography>
        </Box>
      ) : (
        <Stack gap={1}>
          {conRows.map((con) => (
            <ConstructionAccordionRow key={con.id} con={con} db={db} />
          ))}
        </Stack>
      )}
    </Paper>
  );
}

export default function AreaDetailPage({ pageContext }: { pageContext: PageContext }) {
  const { areaId } = useParams<{ areaId: string }>();
  const navigate = useNavigate();
  const { db, loading, reload } = useFullDb(pageContext.api.indexedDB, pageContext.api.currentUser.id);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // 詳細はローカルIDBを読むため、直接URL/リロードでも空にならないよう表示前にサーバ同期する。
  // 同期完了までローディング表示にする(同期前のローカル内容が一瞬表示されるのを防ぐ)。
  const syncing = useMountSync(pageContext.api.isOnline, () => pageContext.api.entities.syncConstructionData(), reload);

  const completions = useMemo(() => deriveAreaProcessCompletions(db), [db.processExecutions]);
  const area = useMemo(() => db.constructionPlanAreas.find((a) => a.id === areaId) ?? null, [db.constructionPlanAreas, areaId]);

  // 登録者名。created_by_user_id はサーバ設定列(キャッシュ同梱)で、名前はオンライン時に解決する。
  const creatorUserId = area?.created_by_user_id ?? null;
  const creatorName = useCreatorName(pageContext, creatorUserId);

  if (loading || syncing) {
    return (
      <Stack p={3} gap={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/console/construction-plan/")} sx={{ alignSelf: "flex-start" }}>施工箇所一覧へ戻る</Button>
        <ContentLoading />
      </Stack>
    );
  }
  if (area === null) {
    return (
      <Stack p={3} gap={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/console/construction-plan/")} sx={{ alignSelf: "flex-start" }}>施工箇所一覧へ戻る</Button>
        <Typography color="text.secondary">施工箇所が見つかりません。</Typography>
      </Stack>
    );
  }

  const work = db.works.find((w) => w.id === area.work_id);
  const wt = db.workTypes.find((w) => w.id === area.work_type_id);
  const wst = db.workSubtypes.find((w) => w.id === area.work_subtype_id);
  const processes = getAreaProcessViews(area, completions, {
    workDetails: db.workDetails,
    mixRatioDefinitions: db.mixRatioDefinitions,
    materials: db.materials,
    plannedMaterials: db.constructionPlanAreaPlannedMaterials,
  });

  const processCount = processes.length;
  const completedCount = processes.filter((p) => p.isCompleted).length;
  const areaStatus = getAreaStatus(area, completions, db.workDetails);
  const statusColor = AREA_STATUS_COLOR[areaStatus];

  // 使用材料: 工程 → 配合比 → material_id を重複排除して材料名を列挙
  const usedMaterialIds = Array.from(new Set(
    processes
      .filter((p) => p.materialId != null)
      .map((p) => p.materialId).filter((id): id is number => id != null),
  ));
  const usedMaterials = usedMaterialIds
    .map((id) => db.materials.find((m) => m.id === id))
    .filter((m): m is NonNullable<typeof m> => m != null);

  // 場所区分: areaDivisionLocationLabels(label_name) × constructionPlanAreaLocations(slot→location_division_id) で分解
  const areaDivisionId = work?.area_division_id ?? null;
  const locationLabels = db.areaDivisionLocationLabels
    .filter((l) => l.deleted_at == null && l.area_division_id === areaDivisionId)
    .sort((a, b) => a.slot - b.slot);
  const areaLocations = db.constructionPlanAreaLocations
    .filter((l) => l.deleted_at == null && l.construction_plan_area_id === area.id);

  // label_name → 対応する location_division.division_name
  const locationInfoCols: InfoListColDef[] = locationLabels.map((lbl) => {
    const locRow = areaLocations.find((l) => l.slot === lbl.slot);
    const divName = locRow
      ? (db.locationDivisions.find((d) => d.id === locRow.location_division_id)?.division_name ?? "-")
      : "-";
    return { label: lbl.label_name, context: divName };
  });

  const deleteLabel = dataEntityText("plan-area", area.id);

  const handleDelete = async () => {
    setDeleteOpen(false);
    try {
      await pageContext.api.construction_plan_area.setDelete({ id: area.id, deleted: true });
      navigate("/console/construction-plan/");
    } catch (e) {
      pageContext.api.showError(`削除に失敗しました: ${errorMessageOf(e)}`);
    }
  };

  const cols: InfoListColDef[] = [
    { label: "ID", context: dataEntityText("plan-area", area.id) },
    { label: "工事", context: <MasterLink type="work" id={work?.id} name={work?.short_name ?? work?.official_name ?? ""} /> },
    { label: "工種", context: <MasterLink type="work-type" id={wt?.id} name={wt?.name ?? ""} /> },
    { label: "種別", context: <MasterLink type="work-subtype" id={wst?.id} name={wst?.name ?? ""} /> },
    {
      label: "使用材料",
      context: usedMaterials.length === 0 ? "-" : (
        <Stack direction="row" gap={0.5} flexWrap="wrap">
          {usedMaterials.map((m, idx) => (
            <span key={m.id}>{idx > 0 && " + "}<MasterLink type="material" id={m.id} name={m.name} /></span>
          ))}
        </Stack>
      ),
    },
    { label: "予定施工数量", context: area.planned_qty == null ? "-" : `${area.planned_qty}${wst?.unit ?? ""}` },
    { label: "予定期間", context: `${fmtDate(area.planned_start)} 〜 ${fmtDate(area.planned_end)}` },
    ...locationInfoCols,
    { label: "工程進捗", context: `${completedCount} / ${processCount} 完了` },
    { label: "施工状態", context: areaStatus },
    { label: "登録日", context: fmtDateTime(area.createdAt) },
    { label: "登録者", context: creatorName },
    { label: "備考", context: area.remarks ?? "-" },
  ];

  return (
    <Stack p={3} gap={3} maxWidth={1100} mx="auto">
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate("/console/construction-plan/")}>施工箇所一覧へ戻る</Button>
        <Typography variant="h4" fontWeight="bold" sx={{ ml: 1 }}>施工箇所情報</Typography>
        <Box flex={1} />
        <Button color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteOpen(true)}>削除</Button>
        <Button variant="contained" size="small" startIcon={<EditIcon />} onClick={() => navigate(`/console/construction-plan/area/${area.id}/edit/`)}>編集</Button>
      </Stack>

      <InfoList cols={cols} columns={3} />

      {/* 進捗カード: 工程完了バーと状態Chip */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" gap={2}>
          <Typography variant="h6" fontWeight={700} sx={{ minWidth: 60 }}>進捗</Typography>
          <Box flex={1}>
            <LinearProgress
              variant="determinate"
              value={processCount === 0 ? 0 : (completedCount / processCount) * 100}
              color={completedCount === processCount && processCount > 0 ? "success" : "primary"}
              sx={{ height: 12, borderRadius: 6 }}
            />
          </Box>
          <Stack direction="row" alignItems="baseline" gap={0.5} sx={{ minWidth: 110, justifyContent: "flex-end" }}>
            <Typography variant="h6" fontWeight={700} color={areaStatus === "完了" ? "success.main" : "primary"}>
              {completedCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">/ {processCount} 工程</Typography>
          </Stack>
          <Chip
            size="small"
            color={statusColor}
            icon={areaStatus === "完了" ? <CheckCircleIcon /> : undefined}
            label={areaStatus}
            sx={{ minWidth: 70 }}
          />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={700} mb={1}>工程一覧 (工程順)</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", width: 40 }}>順</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>工程</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>配合比</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>施工方法</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>攪拌方法</TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="right">予定使用材料量</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>施工</TableCell>
              <TableCell sx={{ fontWeight: "bold", width: 110 }}>状態</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {processes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  工程なし (種別配下の工程マスタを整備してください)
                </TableCell>
              </TableRow>
            ) : processes.map((p) => {
              const mix = p.mixRatioDefinitionId != null
                ? db.mixRatioDefinitions.find((x) => x.id === p.mixRatioDefinitionId)
                : undefined;
              const cm = mix?.construction_method_id != null
                ? db.constructionMethods.find((x) => x.id === mix.construction_method_id)
                : undefined;
              const sm = mix?.stirring_method_id != null
                ? db.stirringMethods.find((x) => x.id === mix.stirring_method_id)
                : undefined;
              const con = p.constructionId
                ? db.constructions.find((c) => c.id === p.constructionId && c.deleted_at == null)
                : undefined;
              const mixLabel = p.mixRatioDefinitionId != null ? deriveMixRatioLabel(db, p.mixRatioDefinitionId) : "";
              // 予定使用材料量: 主材料計画数量。単位はmixRatioDetailsの主材料細目から派生
              const mainDetail = mix
                ? db.mixRatioDetails.filter((d) => d.deleted_at == null && d.mix_ratio_definition_id === mix.id)
                    .sort((a, b) => a.id - b.id)[0]
                : undefined;
              const mainMd = mainDetail
                ? db.materialDetails.find((x) => x.id === mainDetail.material_detail_id)
                : undefined;
              const qtyUnit = mainMd?.content_unit ?? "kg";
              return (
                <TableRow key={p.workDetailId}>
                  <TableCell>{p.sortOrder}</TableCell>
                  <TableCell>
                    <MasterLink type="work-detail" id={p.workDetailId} name={p.workDetailName} />
                  </TableCell>
                  <TableCell>
                    <MasterLink type="mix-ratio" id={mix?.id} name={mixLabel || (mix ? `配合比 #${mix.id}` : "")} fallback="-" />
                  </TableCell>
                  <TableCell>
                    <MasterLink type="construction-method" id={cm?.id} name={cm?.name ?? ""} />
                  </TableCell>
                  <TableCell>
                    <MasterLink type="stirring-method" id={sm?.id} name={sm?.name ?? ""} />
                  </TableCell>
                  <TableCell align="right">
                    {p.plannedMainQty != null ? (
                      `${Number(p.plannedMainQty.toFixed(2))} ${qtyUnit}`
                    ) : p.ambiguousLegacyQty ? (
                      <Stack alignItems="flex-end" gap={0.25}>
                        <Typography variant="body2" color="text.secondary">工程別未設定</Typography>
                        {p.legacyMaterialQty != null && (
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                            (材料単位 {Number(p.legacyMaterialQty.toFixed(2))} {qtyUnit})
                          </Typography>
                        )}
                      </Stack>
                    ) : "-"}
                  </TableCell>
                  <TableCell>
                    {con
                      ? <DataLink type="construction" id={con.id} />
                      : <Typography variant="caption" color="text.secondary">-</Typography>}
                  </TableCell>
                  <TableCell>
                    <Chip size="small"
                      color={p.isCompleted ? "success" : "default"}
                      variant={p.isCompleted ? "filled" : "outlined"}
                      icon={p.isCompleted ? <CheckCircleIcon /> : undefined}
                      label={p.isCompleted ? "完了" : "未完了"} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <RelatedConstructionsTable db={db} areaId={area.id} />

      <DeleteDialog
        open={deleteOpen}
        label={deleteLabel}
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Stack>
  );
}
